/**
 * ChildThreatsTable — threats inherited from child TARAs (hierarchical
 * mode). Read-only data + Relevant checkbox + Justification input.
 *
 * STRIDE filtering uses the shared FilterDrawer primitive (same as
 * ThreatTable). Mirror of step3-scenarios/ChildScenariosTable.tsx.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import CategoryPill from '../../../../components/ui/CategoryPill';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import {
  STRIDE_CATEGORIES,
  STRIDE_VALUE_TO_LABEL,
  type ThreatRecord,
} from './threatFieldSchema';
import styles from './ChildThreatsTable.module.css';
import threatTableStyles from './ThreatTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

const renderStride = (row: ChildThreatView) =>
  row.stride_category ? (
    <CategoryPill
      label={STRIDE_VALUE_TO_LABEL[row.stride_category] ?? row.stride_category}
      size="sm"
    />
  ) : (
    ''
  );

export interface ChildThreatView extends ThreatRecord {}

export interface ChildThreatsTableProps {
  threats: ChildThreatView[];
  onToggleRelevance: (threatId: string) => void;
  onJustificationChange: (threatId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildThreatsTable({
  threats,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildThreatsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [strideValues, setStrideValues] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const strideCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of STRIDE_CATEGORIES) counts[c.value] = 0;
    for (const t of threats) {
      const key = String(t.stride_category ?? '');
      if (key in counts) counts[key] += 1;
    }
    return counts;
  }, [threats]);

  const strideFiltered = useMemo(
    () => (strideValues.length === 0
      ? threats
      : threats.filter((t) => strideValues.includes(String(t.stride_category ?? '')))),
    [threats, strideValues],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return strideFiltered;
    return strideFiltered.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [strideFiltered, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.threat_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'threat_id', label: 'ID', sortable: true, width: '9%' },
      { key: 'stride_category', label: 'STRIDE', sortable: true, width: '13%', render: renderStride },
      { key: 'asset_id', label: 'Target Asset', sortable: true, width: '10%' },
      {
        key: 'damage_ids',
        label: 'Damage IDs',
        sortable: false,
        width: '10%',
        render: (row: ChildThreatView) => joinList(row.damage_ids),
      },
      { key: 'threat_scenario', label: 'Scenario', sortable: false, width: '29%' },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '10%',
        render: (row: ChildThreatView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.threat_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.threat_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '19%',
        render: (row: ChildThreatView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.threat_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  const strideFilter = {
    key: 'stride_category',
    label: 'STRIDE',
    values: strideValues,
    onChange: (next: string[]) => {
      setStrideValues(next);
      setPage(1);
    },
    options: STRIDE_CATEGORIES.map((c) => ({
      value: c.value,
      label: c.label,
      count: strideCounts[c.value] ?? 0,
    })),
  };

  return (
    <div className={threatTableStyles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[strideFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        emptyMessage="No inherited threats from child TARAs."
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search inherited threats…',
            }}
            count={{ value: filtered.length, label: 'Inherited Threats' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || strideValues.length > 0}
            actions={
              <RelevanceEditToggle
                editing={liveEditing}
                onToggle={handleToggle}
                pending={saving}
                disabled={disabled}
              />
            }
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
